import type { Choice, ComboContent } from './types'

const ok = (label: string): Choice => ({ label, correct: true })
const no = (label: string): Choice => ({ label })

export const COMBO_CONTENT: ComboContent[] = [
  {
    key: 'strategy-factory-method',
    slugs: ['strategy', 'factory-method'],
    teach: [
      {
        label: 'Why they pair',
        text: 'Strategy gives you swappable behaviours behind one interface. Factory Method decides which one to build, so callers never name a concrete class.',
      },
      {
        label: 'Pick a driver',
        text: 'Laravel does this for cache, queue and mail drivers: a config value picks the driver, a creator method returns it, and the caller only sees the interface.',
      },
    ],
    quizzes: [
      {
        format: 'quick_pick',
        multi: true,
        prompt:
          'Config says QUEUE=sqs. A manager method returns an SqsQueue, and the app only calls push(). Which patterns?',
        choices: [ok('Strategy'), ok('Factory Method'), no('Adapter'), no('Builder'), no('Singleton')],
        explanation:
          'Queue drivers are strategies; the manager method that builds the chosen one is a factory method.',
      },
      {
        format: 'spot',
        multi: true,
        prompt: 'Which patterns are here?',
        code: `abstract class Checkout {
    abstract function gw(): Gateway;
    public function charge(Order $o): void {
        $this->gw()->pay($o->total);
    }
}
class StripeCheckout extends Checkout {
    protected function gw(): Gateway {
        return new StripeGateway();
    }
}`,
        choices: [ok('Factory Method'), ok('Strategy'), no('Adapter'), no('Builder'), no('Prototype')],
        explanation:
          'gw() is the factory method; Gateway implementations are the interchangeable strategies.',
      },
      {
        format: 'judge',
        prompt: 'Here the Factory Method and the Strategy are the same class.',
        code: `interface Gateway { function pay(int $c); }

class Stripe implements Gateway {
    function pay(int $c) { /* ... */ }
}
class Paypal implements Gateway {
    function pay(int $c) { /* ... */ }
}
function make(string $n): Gateway {
    return $n === 'paypal'
        ? new Paypal() : new Stripe();
}`,
        choices: [no('True'), ok('False')],
        explanation: 'Stripe and Paypal are the strategies; make() is a separate factory that picks one.',
      },
    ],
  },
  {
    key: 'decorator-proxy',
    slugs: ['decorator', 'proxy'],
    teach: [
      {
        label: 'Why they pair',
        text: 'Both wrap an object that has the same interface. Decorator adds behaviour such as logging; Proxy controls access, such as caching or lazy loading.',
      },
      {
        label: 'Around a repository',
        text: 'Stack them over one repository: a caching proxy in front, a logging decorator beside it. Callers still hold a plain UserRepository.',
      },
    ],
    quizzes: [
      {
        format: 'quick_pick',
        multi: true,
        prompt:
          'A CachedUserRepo skips the database on a hit. A LoggedUserRepo logs each call. Both wrap a UserRepo. Which patterns?',
        choices: [ok('Proxy'), ok('Decorator'), no('Adapter'), no('Facade'), no('Composite')],
        explanation: 'The cache guards access like a proxy; the logger layers on behaviour like a decorator.',
      },
      {
        format: 'spot',
        multi: true,
        prompt: 'Which patterns are here?',
        code: `class LoggedRepo implements UserRepo {
    public function __construct(
        private UserRepo $inner,
        private Logger $log,
    ) {}
    public function find(int $id): ?User {
        $this->log->info("find $id");
        return $this->inner->find($id);
    }
}
$repo = new LoggedRepo(new CachedRepo($db));`,
        choices: [ok('Decorator'), ok('Proxy'), no('Adapter'), no('Facade'), no('Bridge')],
        explanation: 'LoggedRepo decorates; CachedRepo, built around the db repo, acts as a caching proxy.',
      },
      {
        format: 'judge',
        prompt: 'These two patterns are doing the same job.',
        code: `class LazyRepo implements UserRepo {
    private ?UserRepo $real = null;
    public function find(int $id): ?User {
        $this->real ??= new SqlRepo();
        return $this->real->find($id);
    }
}`,
        choices: [no('True'), ok('False')],
        explanation: 'Same shape, different intent: a proxy controls access, a decorator adds behaviour.',
      },
    ],
  },
  {
    key: 'composite-visitor',
    slugs: ['composite', 'visitor'],
    teach: [
      {
        label: 'Why they pair',
        text: 'Composite builds a tree out of nodes that share an interface. Visitor lets you add new operations on that tree without editing every node class.',
      },
      {
        label: 'Walking an AST',
        text: 'PHP-Parser parses code into a node tree, and NodeTraverser sends visitors across it. A linter or a rewriter is just another visitor.',
      },
    ],
    quizzes: [
      {
        format: 'quick_pick',
        multi: true,
        prompt:
          'A parser yields a tree of Expr and Stmt nodes. A NameCollector class is passed into each node to gather names. Which patterns?',
        choices: [ok('Composite'), ok('Visitor'), no('Iterator'), no('Interpreter'), no('Builder')],
        explanation: 'The nested nodes form a composite; NameCollector is a visitor that operates on it.',
      },
      {
        format: 'spot',
        multi: true,
        prompt: 'Which patterns are here?',
        code: `interface Node {
    function accept(Visitor $v);
}

class Add implements Node {
    function __construct(Node $l, $r) {}
    function accept(Visitor $v) {
        $this->l->accept($v);
        $this->r->accept($v);
        return $v->visitAdd($this);
    }
}
class Num implements Node { /* leaf */ }`,
        choices: [ok('Composite'), ok('Visitor'), no('Decorator'), no('Command'), no('Flyweight')],
        explanation: 'Add holds child nodes (composite) and accept() hands each one to a visitor.',
      },
      {
        format: 'judge',
        prompt: 'Adding a new operation here means editing every node class.',
        code: `class CountNum extends NodeVisitorAbstract {
    public int $n = 0;
    public function enterNode(Node $node) {
        if ($node instanceof LNumber) {
            $this->n++;
        }
    }
}`,
        choices: [no('True'), ok('False')],
        explanation:
          'That is the visitor payoff: a new operation is a new class, and the nodes stay untouched.',
      },
    ],
  },
  {
    key: 'composite-iterator',
    slugs: ['composite', 'iterator'],
    teach: [
      {
        label: 'Why they pair',
        text: 'A composite is a tree, and callers often just want a flat walk. An Iterator hides the recursion so they can foreach over every node.',
      },
      {
        label: 'Flatten the tree',
        text: 'The iterator keeps the traversal state, such as a stack, outside the nodes. You can offer depth-first and breadth-first without touching the tree.',
      },
    ],
    quizzes: [
      {
        format: 'quick_pick',
        multi: true,
        prompt:
          'A Menu holds items and sub-menus. foreach ($menu as $item) visits every nested entry in order. Which patterns?',
        choices: [ok('Composite'), ok('Iterator'), no('Visitor'), no('Observer'), no('Proxy')],
        explanation: 'Menus nest (composite) and foreach hides the walk through them (iterator).',
      },
      {
        format: 'spot',
        multi: true,
        prompt: 'Which patterns are here?',
        code: `class Dir implements IteratorAggregate {
    private array $children = [];
    public function add(Entry $e): void {
        $this->children[] = $e;
    }
    function getIterator(): Iterator {
        foreach ($this->children as $c) {
            yield $c;
            if ($c instanceof Dir) {
                yield from $c;
            }
        }
    }
}`,
        choices: [ok('Composite'), ok('Iterator'), no('Decorator'), no('Memento'), no('Facade')],
        explanation: 'Dir holds entries including other Dirs; getIterator() walks the whole tree for you.',
      },
    ],
  },
  {
    key: 'command-memento',
    slugs: ['command', 'memento'],
    teach: [
      {
        label: 'Why they pair',
        text: 'Command turns an action into an object you can store and replay. Memento captures the state before it, so the same action can be undone.',
      },
      {
        label: 'Undo',
        text: 'Each command takes a memento before it runs, and undo() restores it. A history stack of commands is then an undo stack.',
      },
    ],
    quizzes: [
      {
        format: 'quick_pick',
        multi: true,
        prompt:
          'Each edit is an object on a history stack. Before it runs, it saves a snapshot of the document so undo can restore it. Which patterns?',
        choices: [ok('Command'), ok('Memento'), no('State'), no('Observer'), no('Prototype')],
        explanation: 'Edits as stored objects are commands; the saved snapshot is the memento.',
      },
      {
        format: 'spot',
        multi: true,
        prompt: 'Which patterns are here?',
        code: `type DeleteLine struct {
	doc  *Doc
	prev Snapshot
}

func (c *DeleteLine) Do() {
	c.prev = c.doc.Save()
	c.doc.RemoveLine()
}

func (c *DeleteLine) Undo() {
	c.doc.Restore(c.prev)
}`,
        choices: [ok('Command'), ok('Memento'), no('Strategy'), no('State'), no('Mediator')],
        explanation: 'DeleteLine is a command with Do and Undo; prev is the memento it restores from.',
      },
      {
        format: 'judge',
        prompt: 'Here the memento is the command itself.',
        code: `$cmd = new Rename($file, 'b.txt');
$cmd->execute();
$history->push($cmd);

// later
$history->pop()->undo();`,
        choices: [no('True'), ok('False')],
        explanation: 'The command does the work; a memento would be the saved state that undo() puts back.',
      },
    ],
  },
  {
    key: 'observer-mediator',
    slugs: ['observer', 'mediator'],
    teach: [
      {
        label: 'Why they pair',
        text: 'Observers react to changes; a Mediator sits in the middle so they never need to know each other. Together, publishers and subscribers meet at a hub.',
      },
      {
        label: 'Through a hub',
        text: 'The Kubernetes API server is the hub: controllers watch it and write to it, never to each other. Laravel events work the same way.',
      },
    ],
    quizzes: [
      {
        format: 'quick_pick',
        multi: true,
        prompt:
          'Controllers never call each other. They watch the API server for changes and write results back to it. Which patterns?',
        choices: [ok('Observer'), ok('Mediator'), no('Chain of Responsibility'), no('Proxy'), no('Facade')],
        explanation: 'Watching for changes is observer; routing everything through one hub is mediator.',
      },
      {
        format: 'spot',
        multi: true,
        prompt: 'Which patterns are here?',
        code: `type Hub struct {
	subs map[string][]Handler
}

func (h *Hub) On(t string, f Handler) {
	h.subs[t] = append(h.subs[t], f)
}

func (h *Hub) Emit(t string, e Event) {
	for _, f := range h.subs[t] {
		f(e)
	}
}`,
        choices: [ok('Observer'), ok('Mediator'), no('Command'), no('Singleton'), no('State')],
        explanation: 'On() subscribes and Emit() notifies; the Hub sits between all senders and listeners.',
      },
    ],
  },
  {
    key: 'abstract-factory-singleton',
    slugs: ['abstract-factory', 'singleton'],
    teach: [
      {
        label: 'Why they pair',
        text: 'An Abstract Factory builds a whole matching family of objects. You rarely need two of them, so it is often held as a Singleton.',
      },
      {
        label: 'One factory',
        text: 'In Laravel, the container binds one factory per app, and every caller gets that same instance. Swap it in tests to change the whole family at once.',
      },
    ],
    quizzes: [
      {
        format: 'quick_pick',
        multi: true,
        prompt:
          'One UiFactory makes buttons, inputs and menus in a matching theme. The app creates it once and shares it everywhere. Which patterns?',
        choices: [
          ok('Abstract Factory'),
          ok('Singleton'),
          no('Factory Method'),
          no('Prototype'),
          no('Flyweight'),
        ],
        explanation:
          'A family of related products is an abstract factory; one shared instance is a singleton.',
      },
      {
        format: 'spot',
        multi: true,
        prompt: 'Which patterns are here?',
        code: `interface WidgetFactory {
    function button(): Button;
    function input(): Input;
}
class DarkFactory implements WidgetFactory {
    private static ?self $one = null;
    static function get(): self {
        return self::$one ??= new self();
    }
    function button(): Button { /* dark */ }
    function input(): Input { /* dark */ }
}`,
        choices: [ok('Abstract Factory'), ok('Singleton'), no('Builder'), no('Bridge'), no('Proxy')],
        explanation:
          'Button and input come from one factory family, and get() always returns the single instance.',
      },
      {
        format: 'judge',
        prompt: 'This binds one shared factory for the whole app.',
        code: `$this->app->singleton(
    WidgetFactory::class,
    fn () => new DarkFactory(),
);

$ui = app(WidgetFactory::class);`,
        choices: [ok('True'), no('False')],
        explanation: 'singleton() in the container hands every caller the same factory instance.',
      },
    ],
  },
  {
    key: 'chain-of-responsibility-command',
    slugs: ['chain-of-responsibility', 'command'],
    teach: [
      {
        label: 'Why they pair',
        text: 'A chain passes a request through handlers that can each stop it or pass it on. A Command packages the work that the last handler finally runs.',
      },
      {
        label: 'Middleware to job',
        text: 'Laravel middleware check auth and throttling in order. The last link then dispatches a job, a command object that a worker runs later.',
      },
    ],
    quizzes: [
      {
        format: 'quick_pick',
        multi: true,
        prompt:
          'Requests pass auth, throttle and CSRF middleware; any can reject. The controller then dispatches a SendInvoice job. Which patterns?',
        choices: [
          ok('Chain of Responsibility'),
          ok('Command'),
          no('Decorator'),
          no('Observer'),
          no('Mediator'),
        ],
        explanation: 'Middleware that can stop or pass on is a chain; the queued job is a command object.',
      },
      {
        format: 'spot',
        multi: true,
        prompt: 'Which patterns are here?',
        code: `class EnsureActive {
    function handle($r, Closure $next) {
        if (! $r->user()->active) {
            abort(403);
        }
        return $next($r);
    }
}
// controller, at the end of the chain
SendInvoice::dispatch($order);`,
        choices: [ok('Chain of Responsibility'), ok('Command'), no('Proxy'), no('Strategy'), no('Facade')],
        explanation: 'handle() either aborts or calls $next; SendInvoice is a job, a command to run later.',
      },
    ],
  },
  {
    key: 'template-method-strategy',
    slugs: ['template-method', 'strategy'],
    teach: [
      {
        label: 'Why they pair',
        text: 'Template Method fixes the steps of an algorithm in a base class. Strategy lets one step be swapped by passing in an object instead of subclassing.',
      },
      {
        label: 'Inherit or inject',
        text: 'Use Template Method when the order is fixed and subclasses fill gaps. Hand a step to a Strategy when it should change at runtime.',
      },
    ],
    quizzes: [
      {
        format: 'quick_pick',
        multi: true,
        prompt:
          'An Exporter base class runs open, write rows, close in fixed order. The row format is an injected Formatter object. Which patterns?',
        choices: [ok('Template Method'), ok('Strategy'), no('Builder'), no('State'), no('Bridge')],
        explanation:
          'The fixed skeleton is a template method; the injected Formatter is a swappable strategy.',
      },
      {
        format: 'spot',
        multi: true,
        prompt: 'Which patterns are here?',
        code: `abstract class Report {
    public function __construct(
        private Formatter $fmt,
    ) {}
    final public function run(): string {
        $rows = $this->load();
        $out = $this->fmt->format($rows);
        return $this->wrap($out);
    }
    abstract protected function load();
    protected function wrap($s) {
        return $s;
    }
}`,
        choices: [
          ok('Template Method'),
          ok('Strategy'),
          no('Factory Method'),
          no('Decorator'),
          no('Visitor'),
        ],
        explanation: 'run() fixes the steps with hooks to override; $fmt is a strategy chosen from outside.',
      },
      {
        format: 'judge',
        prompt: 'Both patterns let you vary part of an algorithm.',
        choices: [ok('True'), no('False')],
        explanation: 'Template Method varies it by subclassing, Strategy by composition.',
      },
    ],
  },
]
